/**
 * /dashboard/settings/asset-types — Settings → People & HR → Asset types
 * (SOP §8 + §11.3).
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port of
 * frontend/src/app/(dashboard)/dashboard/settings/asset-types/page.tsx.
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import { useAssetTypes } from "@/hooks/use-hr";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { AssetTypeForm } from "@/components/hr/asset-type-form";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { RestoreDefaultsButton } from "@/components/settings/restore-defaults-button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/asset-types",
)({
  component: SettingsAssetTypesRoute,
});

function SettingsAssetTypesRoute() {
  const { isHrAdmin } = useHrRoles();
  const q = useAssetTypes();

  const types = useMemo(
    () =>
      [...(q.data?.asset_types ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [q.data],
  );

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Asset types</h2>
              <p className="text-sm text-muted-foreground">
                Categories of company asset (Laptop, Phone, etc.).
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isHrAdmin && (
                <RestoreDefaultsButton
                  catalog="asset-types"
                  catalogLabel="asset types"
                  invalidateKeys={[["hr", "asset-types"]]}
                />
              )}
              {isHrAdmin && <AssetTypeForm />}
            </div>
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can change asset types.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3", "s4"].map((k) => (
                <Skeleton key={k} className="h-16 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load asset types</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && types.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No asset types yet.
                {isHrAdmin && " Use Restore defaults to seed the standard set."}
              </CardContent>
            </Card>
          )}

          {q.data && types.length > 0 && (
            <div className="space-y-3">
              {types.map((t) => (
                <Card key={t.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="text-base">{t.name}</CardTitle>
                        {t.description && (
                          <p className="mt-1 text-sm text-muted-foreground">
                            {t.description}
                          </p>
                        )}
                      </div>
                      {isHrAdmin && <AssetTypeForm existing={t} />}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
